import { usePublicChatContext } from "@lazarentertainment/public-chat-web/dist/src/react"
import {
  Chat,
  ClosedCaption,
  Maximize,
  Minimize,
  Share,
  VolumeOn,
  VolumeX,
} from "@lazarentertainment/web-design-system/components/iconOutline"
import { ClosedCaptionSolid } from "@lazarentertainment/web-design-system/components/iconSolid"
import { useChannelContext } from "client/components/channel"
import {
  isFullscreenSupported,
  useFullscreenContext,
} from "client/components/layout/FullscreenContext"
import { BrandWatermark } from "client/components/main/BrandWatermark"
import { Button } from "client/designSystem/Button"
import { useChatContext } from "client/packages/chat/components/ChatContext"
import ChatHistory from "client/packages/chat/components/chatHistory"
import { ChatInput } from "client/packages/chat/components/chatInput"
import { MobileChatModalLayer } from "client/packages/chat/components/MobileChatModalLayer"
import { ChatView } from "client/packages/chat/constants"
import { useMobileChatInput } from "client/packages/chat/useMobileChatInput"
import { CommerceFeaturedProductButton } from "client/packages/commerce/components/CommerceFeaturedProductButton"
import { TargetFeaturedProductButton } from "client/packages/target/components/TargetFeaturedProductButton"
import { useGlobalContext } from "client/packages/utils/GlobalContext"
import { ShareDialog } from "client/views/ShareView/ShareDialog"
import { PluginButtonList } from "shared/components/PluginButtonList"

import { PlayPause } from "../ui/videoControls"
import { PlaybackScrubBar } from "../ui/videoControls/PlaybackScrubBar"
import { Timestamp } from "../ui/videoControls/Timestamp"
import { useMobileControlsOverlay } from "../ui/videoControls/useMobileControlsOverlay"

import { LightboxDismissButton } from "./LightboxDismissButton"
import { SubtitlePlaceholderArea } from "./SubtitlePlaceholderArea"
import { VideoBadge } from "./VideoBadge"
import { useVideoContext } from "./VideoContext"

import styles from "./MobileVideoOverlayLayer.module.css"

export type MobileVideoOverlayProps = {
  brandWatermark?: boolean
}

export const MobileVideoOverlayLayer = ({
  brandWatermark = false,
}: MobileVideoOverlayProps) => {
  const {
    videoElement,
    isPlaying,
    videoTimestamp,
    subtitlesVisible,
    setSubtitlesVisible,
    isMuted,
    mutedPreview,
  } = useVideoContext()
  const { recordingId, live, liveSubtitles } = useChannelContext()
  const {
    messages,
    isChatOpen,
    setIsChatOpen,
    isJoinModalOpen,
    clearChatError,
  } = useChatContext()
  const { reactions, toggleReaction, banUser, deleteMessage } =
    usePublicChatContext()
  const {
    state: { isChatPluginAvailable, lightboxContent },
  } = useGlobalContext()

  const { isFullscreen, enterFullscreen, exitFullscreen } =
    useFullscreenContext()

  // Set up chat input focus stuff. Then attach these refs to the right components
  const { inputRef, chatInputWrapperFocusProps } = useMobileChatInput()

  const shouldShowPlaybackUI = live && !mutedPreview

  // Subtitles are available for VODs, or for live video with subtitles enabled
  const shouldShowSubtitlesButton = recordingId || liveSubtitles

  // If in a lightbox (on mobile), don't show the fullscreen option
  const shouldShowFullscreenButton =
    isFullscreenSupported && lightboxContent.contentType === "none"

  const {
    showControlsOverlay,
    pointerDetectionAreaRef,
    ...pointerEventHandlers
  } = useMobileControlsOverlay({
    controlsEnabled: shouldShowPlaybackUI,
    isPlaying,
    hideDelayMs: 1500,
  })

  return (
    <div
      className={styles.mainContainer}
      data-show-chat-gradient={isChatPluginAvailable}
    >
      <div
        className={styles.controlsOverlayBackground}
        data-visible={showControlsOverlay}
      />
      <div className={styles.topSection}>
        <div className={styles.leftSection}>
          <div className={styles.topRow}>
            {!isChatOpen && <VideoBadge />}
            {shouldShowPlaybackUI && !isChatOpen && (
              <CommerceFeaturedProductButton />
            )}
            {shouldShowPlaybackUI && !isChatOpen && (
              <TargetFeaturedProductButton />
            )}
          </div>

          {shouldShowPlaybackUI &&
            !isChatOpen &&
            /*
             * We don't show the play/pause button while live (and unpaused), only because
             * there's a playback freeze bug on android Chrome when unpausing a live video.
             * https://github.com/lazarentertainment/broadcast-viewer/issues/2476
             * if we figure that out maybe we can re-enable play/pause while live.
             */
            (recordingId || !isPlaying) && (
              <div
                className={styles.pointerDetectionArea}
                ref={pointerDetectionAreaRef}
                {...pointerEventHandlers}
                data-visible={showControlsOverlay}
              >
                <div className={styles.controlsContainer}>
                  <PlayPause
                    size="4xl"
                    isPlaying={isPlaying}
                    play={() => {
                      videoElement?.play()
                    }}
                    pause={() => {
                      videoElement?.pause()
                    }}
                  />
                  {recordingId && (
                    <div className={styles.controlsScrubRow}>
                      <Timestamp currentTime={videoTimestamp} />
                      <div className={styles.controlsScrubBarContainer}>
                        <PlaybackScrubBar
                          currentTime={videoTimestamp}
                          duration={videoElement?.duration ?? 0}
                          onCurrentTimeChange={(newTime) => {
                            if (videoElement) {
                              videoElement.currentTime = newTime
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          {shouldShowPlaybackUI && (
            <div className={styles.bottomSection}>
              {isChatPluginAvailable && (
                <div className={styles.chatHistoryContainer}>
                  <ChatHistory
                    uiView={ChatView.OVERLAY}
                    messages={messages}
                    messageReactions={reactions}
                    submitMessageReaction={toggleReaction}
                    banUser={banUser}
                    deleteChatMessage={deleteMessage}
                  />
                </div>
              )}
              {subtitlesVisible && <SubtitlePlaceholderArea />}
            </div>
          )}
        </div>
        {shouldShowPlaybackUI && !isChatOpen && (
          <div className={styles.rightSection}>
            {lightboxContent.contentType !== "none" && (
              <LightboxDismissButton />
            )}
            <ShareDialog
              trigger={
                <Button
                  variant="overlay"
                  icon={Share}
                  iconOnly
                  size="md"
                  data-robot="share-button"
                />
              }
            />
            {shouldShowSubtitlesButton && (
              <Button
                variant="overlay"
                icon={
                  subtitlesVisible ? ClosedCaptionSolid : ClosedCaption
                }
                iconOnly
                size="md"
                data-robot="toggle-subtitles-button"
                onClick={() => {
                  setSubtitlesVisible(!subtitlesVisible)
                }}
              />
            )}
            <Button
              variant="overlay"
              icon={isMuted ? VolumeX : VolumeOn}
              iconOnly
              size="md"
              data-robot="toggle-sound-button"
              onClick={() => {
                if (videoElement) {
                  videoElement.muted = !isMuted
                }
              }}
            />
            {shouldShowFullscreenButton && (
              <Button
                variant="overlay"
                icon={isFullscreen ? Minimize : Maximize}
                iconOnly
                size="md"
                data-robot="toggle-fullscreen-button"
                onClick={isFullscreen ? exitFullscreen : enterFullscreen}
              />
            )}
          </div>
        )}
      </div>
      {shouldShowPlaybackUI && (
        <div className={styles.bottomRow}>
          {isChatOpen ? (
            <div
              className={styles.chatInputWrapper}
              {...chatInputWrapperFocusProps}
            >
              <ChatInput
                autoFocus={!isJoinModalOpen}
                inputRef={inputRef}
                uiView={ChatView.OVERLAY}
              />
            </div>
          ) : (
            <>
              {!recordingId && isChatPluginAvailable && !isChatOpen && (
                <Button
                  variant="overlay"
                  icon={Chat}
                  iconOnly
                  size="md"
                  data-robot="toggle-chat-button"
                  onClick={() => {
                    setIsChatOpen(true)
                    clearChatError()
                  }}
                />
              )}
            </>
          )}
          {!isChatOpen && (
            <div className={styles.pluginButtonsWrapper}>
              <PluginButtonList />
            </div>
          )}
        </div>
      )}
      {brandWatermark && live && (
        <div className={styles.brandWatermarkContainer}>
          <BrandWatermark />
        </div>
      )}
      {isChatPluginAvailable && (
        <MobileChatModalLayer
          focusMainChatInput={() => {
            inputRef.current?.focus()
          }}
        />
      )}
    </div>
  )
}
